import { inject, Injectable } from "@angular/core";
import config from "devextreme/core/config";
import { MODULES, MODULES_BY_REGION } from "src/app/core/modules";
import { LocaleService } from "src/app/services/locale.service";
import { dateUtilSetTimeZone } from "src/app/support/date-util";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { RatetableItem } from "src/app/modules/bootstrap/pms-ui/components/ui/ratetable-toolbar/ratetable-toolbar.component";

export enum Regions {
  EN = "en",
  IT = "it",
  ES = "es",
  AE = "ae",
  FR = "fr",
  CH = "ch",
  KA = "ka" // georgia
}

@Injectable()
export class RegionService {
  private readonly localeService = inject(LocaleService);

  private current: Regions = Regions.EN;

  set(region: Regions): void {
    this.current = <Regions>region.toLowerCase();
  }

  setByString(region: string): void {
    this.set(<Regions>region.toLowerCase());

    switch (this.current) {
      case Regions.CH: {
        config({
          defaultCurrency: "CHF"
        });
        break;
      }
      case Regions.EN: {
        config({
          defaultCurrency: "GBP"
        });
        break;
      }
      case Regions.AE: {
        config({
          defaultCurrency: "AED"
        });
        break;
      }
      case Regions.IT: {
        config({
          defaultCurrency: "EUR"
        });

        break;
      }
      case Regions.KA: {
        config({
          defaultCurrency: "GEL"
        });

        break;
      }
      default: {
        config({
          defaultCurrency: "EUR"
        });
        break;
      }
    }
  }

  setTimezone(timezone: string): void {
    dateUtilSetTimeZone(timezone);
  }

  get(): Regions {
    return this.current;
  }

  getCountry(): string {
    return this.get().toUpperCase();
  }

  getLocale(): string {
    return this.localeService.getCurrent();
  }

  getShortDateParseFormatByLocale(): string {
    switch (this.current) {
      case Regions.CH:
      case Regions.FR:
      case Regions.IT:
      case Regions.KA: {
        return "DD/MM/YYYY";
      }
      case Regions.EN:
      case Regions.ES:
      case Regions.AE: {
        return "MM/DD/YYYY";
      }
    }
  }

  getDefaultVatByRegion(ratetableItem?: RatetableItem): number {
    switch (this.current) {
      case Regions.AE: {
        return 0;
      }
      case Regions.CH: {
        switch (ratetableItem) {
          case RatetableItem.DRUG: {
            return 0.026;
          }
          default: {
            return 0.081;
          }
        }
      }
      default: {
        return 0.22;
      }
    }
  }

  getVatsByRegion(): Array<{ value: number; display: string }> {
    switch (this.current) {
      case Regions.AE: {
        return [
          {
            value: 0.05,
            display: sntTranslate(marker("5%"))
          },
          {
            value: 0,
            display: sntTranslate(marker("0%"))
          }
        ];
      }
      case Regions.CH: {
        return [
          {
            value: 0.081,
            display: sntTranslate(marker("8,1%"))
          },
          {
            value: 0.026,
            display: sntTranslate(marker("2,6%"))
          }
        ];
      }
      default: {
        return [
          {
            value: 0.22,
            display: sntTranslate(marker("22%"))
          },
          {
            value: 0.1,
            display: sntTranslate(marker("10%"))
          },
          {
            value: 0.05,
            display: sntTranslate(marker("5%"))
          },
          {
            value: 0.04,
            display: sntTranslate(marker("4%"))
          },
          {
            value: 0,
            display: sntTranslate(marker("0%"))
          }
        ];
      }
    }
  }

  async canBeActivated(module: MODULES): Promise<boolean> {
    const currentConfig = MODULES_BY_REGION[this.current];
    if (!currentConfig) {
      return false;
    }

    return currentConfig.includes(module);
  }

  canBeActivatedSync(module: MODULES): boolean {
    const currentConfig = MODULES_BY_REGION[this.current];
    if (!currentConfig) {
      return false;
    }

    return currentConfig.includes(module);
  }

  getCurrency(): string {
    switch (this.current.toLowerCase()) {
      case Regions.CH: {
        return "CHF";
      }
      case Regions.EN: {
        return "GBP";
      }
      case Regions.AE: {
        return "AED";
      }
      case Regions.KA: {
        return "GEL";
      }
      case Regions.IT:
      default: {
        return "EUR";
      }
    }
  }

  getCurrencySymbol(): string {
    return (0)
      .toLocaleString("en", { style: "currency", currency: this.getCurrency() })
      .replace("0.00", "")
      .trim();
  }
}
